import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/lib/auth";
import { TelegramLogo, Globe, Clock, FloppyDisk } from "@phosphor-icons/react";

export default function Settings() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const { admin } = useAuth();
  const canEdit = admin?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/settings");
    setData(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.put("/settings", {
        main_channel: data.main_channel || "",
        timezone: data.timezone || "",
      });
      setMsg("Сохранено");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500 text-sm">Загрузка…</div>;

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <PageHeader
        title="Настройки"
        subtitle="Параметры приложения и бота"
      />

      <div className="space-y-4">
        <Section
          icon={TelegramLogo}
          title="Telegram Bot"
          description="Токен бота читается из переменной окружения и недоступен для изменения через интерфейс."
        >
          <Field label="Токен">
            <input
              disabled
              value={data.bot_token_masked || "—"}
              className="w-full bg-zinc-900/50 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono text-zinc-500 cursor-not-allowed"
            />
          </Field>
          <Field label="Основной канал">
            <input
              data-testid="settings-main-channel"
              disabled={!canEdit}
              value={data.main_channel || ""}
              onChange={(e) =>
                setData({ ...data, main_channel: e.target.value })
              }
              placeholder="@channel"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
            />
          </Field>
        </Section>

        <Section icon={Clock} title="Время и регион">
          <Field label="Часовой пояс">
            <input
              data-testid="settings-timezone"
              disabled={!canEdit}
              value={data.timezone || ""}
              onChange={(e) => setData({ ...data, timezone: e.target.value })}
              placeholder="Europe/Moscow"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
            />
          </Field>
        </Section>

        {canEdit && (
          <div className="flex items-center gap-3">
            <button
              data-testid="save-settings-btn"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md disabled:opacity-50"
            >
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Сохраняем…" : "Сохранить"}
            </button>
            {msg && (
              <span
                data-testid="settings-msg"
                className="text-xs text-emerald-400 font-mono"
              >
                {msg}
              </span>
            )}
          </div>
        )}
        {!canEdit && (
          <div className="text-xs text-zinc-500">
            Только Главный администратор может изменять настройки.
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-md bg-blue-500/15 text-blue-500 flex items-center justify-center">
          <Icon size={16} weight="bold" />
        </div>
        <div>
          <div className="font-semibold">{title}</div>
          {description && (
            <div className="text-xs text-zinc-500 mt-0.5">{description}</div>
          )}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
